import React, { useEffect, useState } from "react";
import { Target, Search, Loader2, ArrowRight, Vote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { sr } from "../../lib/sr";
import AudienceBrowser from "../../components/smartreach/AudienceBrowser";

// Verticals that have a real audience catalogue loaded.
const CATALOG_INDUSTRIES = [
  ["doctors", "Medical & Dental"],
  ["medical-professionals", "Medical Professionals"],
  ["real-estate", "Real Estate"],
  ["commercial-real-estate", "Commercial Real Estate"],
  ["apartments", "Apartments & Multifamily"],
  ["property-management", "Property Management"],
  ["restaurants", "Restaurants & QSR"],
  ["retail", "Retail & Grocery"],
  ["auto", "Auto Dealerships"],
  ["home-services", "Home Services"],
  ["gyms-fitness", "Gyms & Fitness"],
  ["professional-services", "Professional Services"],
  ["political", "Political (TotalReach)"],
  ["luxury", "Luxury Products"],
];
// Cross-industry layers + catch-all — usable alongside any vertical.
const CROSS_INDUSTRY = [
  ["geo", "Geo Targeting (all industries)"],
  ["demographics", "Demographics (all industries)"],
];
const OTHER_CATALOGS = [
  ["unclassified", "Other / Unclassified"],
];
const LABELS = Object.fromEntries([...CATALOG_INDUSTRIES, ...CROSS_INDUSTRY, ...OTHER_CATALOGS]);

export default function AppAudiences() {
  const navigate = useNavigate();
  const [industry, setIndustry] = useState("");
  const [ready, setReady] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");

  // Global "search all catalogues" state
  const [gq, setGq] = useState("");
  const [gres, setGres] = useState(null);
  const [gbusy, setGbusy] = useState(false);

  useEffect(() => {
    sr.businessModel()
      .then((bm) => {
        const ind = (bm?.industry || "").trim();
        setIndustry(LABELS[ind] ? ind : "doctors");
      })
      .catch(() => setIndustry("doctors"))
      .finally(() => setReady(true));
  }, []);

  const runGlobalSearch = async () => {
    const q = gq.trim();
    if (q.length < 2) { toast.message("Type at least 2 characters to search."); return; }
    setGbusy(true);
    try {
      const r = await sr.searchAllAudiences(q, 5);
      setGres(r);
      if (!(r.groups || []).length) toast.message("No segments matched across any catalogue.");
    } catch { toast.error("Search failed"); } finally { setGbusy(false); }
  };

  const openInCatalog = (slug) => {
    setIndustry(slug);
    setInitialQuery(gq.trim());
    setGres(null);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
  };

  const pickIndustry = (slug) => { setIndustry(slug); setInitialQuery(""); setGres(null); };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="app-audiences-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}><Target size={22} /> Audiences</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Browse your real audience catalogue, estimate reach by budget and frequency, compare segments and attach them to your campaign brief.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <button className="sr-btn sr-btn-sm" onClick={() => navigate("/app/political-studio")} data-testid="goto-political-studio" style={{ background: "#fff" }}><Vote size={14} /> Political Studio</button>
          <div>
            <label className="sr-eyebrow" style={{ display: "block", marginBottom: 4 }}>Industry</label>
            <select className="sr-select" value={industry} onChange={(e) => pickIndustry(e.target.value)} data-testid="audiences-industry-picker" style={{ minWidth: 240 }}>
              <optgroup label="Industry verticals">
                {CATALOG_INDUSTRIES.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
              </optgroup>
              <optgroup label="Cross-industry layers">
                {CROSS_INDUSTRY.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
              </optgroup>
              <optgroup label="Other">
                {OTHER_CATALOGS.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
              </optgroup>
            </select>
          </div>
        </div>
      </div>

      {/* Global search across every catalogue */}
      <div className="sr-card" style={{ marginTop: 14, padding: 14 }} data-testid="catalog-search-all">
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Search size={16} style={{ color: "var(--muted)" }} />
          <strong style={{ color: "var(--navy)", fontSize: 14 }}>Search all catalogues</strong>
          <span className="sr-muted" style={{ fontSize: 12 }}>Find a segment across every vertical at once — we'll tell you which catalogue it's in.</span>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
            <input className="sr-input" value={gq} onChange={(e) => setGq(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runGlobalSearch(); }}
              placeholder="e.g. dermatology, movers, luxury auto…" data-testid="search-all-input" style={{ minWidth: 260 }} />
            <button className="sr-btn sr-btn-sm sr-btn-gold" onClick={runGlobalSearch} disabled={gbusy} data-testid="search-all-btn">
              {gbusy ? <Loader2 size={14} className="sr-spin" /> : <Search size={14} />} Search
            </button>
          </div>
        </div>

        {gres && (gres.groups || []).length > 0 && (
          <div style={{ marginTop: 12 }} data-testid="search-all-results">
            <div className="sr-muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
              {gres.total.toLocaleString()} matches for "<strong>{gres.q}</strong>" across {gres.groups.length} catalogue{gres.groups.length === 1 ? "" : "s"}
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {gres.groups.map((g) => (
                <div key={g.industry} className="sr-card" style={{ padding: 12, background: "#fff" }} data-testid={`search-group-${g.industry}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <strong style={{ color: "var(--navy)", fontSize: 13.5 }}>{g.industry_label}</strong>
                    <span className="sr-badge sr-badge-blue" style={{ fontSize: 10 }}>{g.total.toLocaleString()} match{g.total === 1 ? "" : "es"}</span>
                    <button className="sr-btn sr-btn-sm" onClick={() => openInCatalog(g.industry)} data-testid={`search-open-${g.industry}`} style={{ marginLeft: "auto", background: "#F3F5F8" }}>
                      Open in {g.industry_label} <ArrowRight size={13} />
                    </button>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {g.items.map((it) => (
                      <span key={it.id} className="sr-badge sr-badge-gray" style={{ fontSize: 11 }} title={it.type_title}>{it.name}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        {ready && industry && (
          <AudienceBrowser key={`${industry}:${initialQuery}`} industry={industry} industryLabel={LABELS[industry]} initialQuery={initialQuery} defaultBudget={5000} />
        )}
      </div>
    </div>
  );
}
